import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import { UbicacionConfiguracionInicial } from "./UbicacionConfiguracionInicial";
import { normalizarUbicacion } from "../Utils/ubicacionConfiguracionInicial";
import { nombreRegistro, opcionesVisibles, pistaCampo } from "../Utils/presentacionConfiguracionInicial";
import {
  CODIGO_OCURRENCIA_OMISION_PREGUNTA,
  CODIGO_OCURRENCIA_UNICA,
  contenidoRelacionadoDe,
  crearClaveRespuesta,
  ESTADOS_RESPUESTA,
  obtenerPrimero,
  parsearJsonSeguro,
  serializarRespuesta,
} from "../Utils/configuracionInicial.constants";

const contenidoDe = (respuesta) => parsearJsonSeguro(
  obtenerPrimero(respuesta, ["ContenidoRespuestaJson", "contenidoRespuestaJson", "ValorRespuestaJson", "valorRespuestaJson"], null),
  null
);

const convertirCantidadEmpleadosAnterior = (elemento, contenido) => {
  if (elemento.Codigo !== "USR-01" || !contenido?.fields) return contenido;
  const cantidad = Number(contenido.fields["USR-01.CANTIDAD_PERSONAS"]);
  const codigo = cantidad === 1 ? "USR-01.SOLO_YO"
    : cantidad >= 2 && cantidad <= 5 ? "USR-01.DE_2_A_5"
    : cantidad >= 6 && cantidad <= 10 ? "USR-01.DE_6_A_10"
    : cantidad > 10 ? "USR-01.MAS_DE_10" : null;
  return codigo ? { selectedOptionIds: [codigo] } : { selectedOptionIds: [] };
};

const contenidoCompatible = (elemento, respuesta) => {
  const contenido = convertirCantidadEmpleadosAnterior(elemento, contenidoDe(respuesta));
  if (elemento.Codigo === "EMP-05") return normalizarUbicacion(contenido);
  if (!contenido || !["SINGLE_CHOICE", "MULTIPLE_CHOICE", "SINGLE_CHOICE_WITH_NOTE"].includes(elemento.TipoRespuesta)) {
    return contenido;
  }
  const codigosVigentes = new Set((elemento.Opciones || []).map((opcion) => opcion.Codigo));
  const seleccionadas = Array.isArray(contenido.selectedOptionIds)
    ? contenido.selectedOptionIds.filter((codigo) => codigosVigentes.has(codigo))
    : [];
  const detalles = Object.fromEntries(Object.entries(contenido.optionDetails || {})
    .filter(([codigo]) => seleccionadas.includes(codigo)));
  return {
    ...contenido,
    selectedOptionIds: seleccionadas,
    ...(contenido.optionDetails ? { optionDetails: detalles } : {}),
  };
};

const contenidoRelacionadoCompatible = (elemento, relacionado) => {
  const contenido = convertirCantidadEmpleadosAnterior(elemento, contenidoRelacionadoDe(relacionado));
  if (elemento.Codigo === "EMP-05") return normalizarUbicacion(contenido);
  if (!contenido || !["SINGLE_CHOICE", "MULTIPLE_CHOICE", "SINGLE_CHOICE_WITH_NOTE"].includes(elemento.TipoRespuesta)) {
    return contenido;
  }
  const codigosVigentes = new Set((elemento.Opciones || []).map((opcion) => opcion.Codigo));
  const seleccionadas = Array.isArray(contenido.selectedOptionIds)
    ? contenido.selectedOptionIds.filter((codigo) => codigosVigentes.has(codigo))
    : [];
  return { ...contenido, selectedOptionIds: seleccionadas };
};

const respuestaContieneOpcionesRetiradas = (elemento, respuesta) => {
  const contenido = contenidoDe(respuesta);
  if (!contenido || !["SINGLE_CHOICE", "MULTIPLE_CHOICE", "SINGLE_CHOICE_WITH_NOTE"].includes(elemento.TipoRespuesta)) return false;
  const codigosVigentes = new Set((elemento.Opciones || []).map((opcion) => opcion.Codigo));
  return Array.isArray(contenido.selectedOptionIds)
    && contenido.selectedOptionIds.some((codigo) => !codigosVigentes.has(codigo));
};

const estadoDe = (respuesta) => obtenerPrimero(
  respuesta,
  ["EstadoRespuesta", "estadoRespuesta", "Estado", "estado"],
  ESTADOS_RESPUESTA.SIN_RESPONDER
);

const etiquetaEstado = {
  [ESTADOS_RESPUESTA.RESPONDIDA]: ["Respondida", "success"],
  [ESTADOS_RESPUESTA.PARA_DESPUES]: ["Para después", "warning"],
  [ESTADOS_RESPUESTA.OMITIDA]: ["Omitida", "secondary"],
  [ESTADOS_RESPUESTA.SIN_RESPONDER]: ["Pendiente", "light"],
};

const EstadoRespuesta = ({ estado }) => {
  const [texto, variante] = etiquetaEstado[estado] || [estado || "Pendiente", "light"];
  return <span className={`ci-answer-state ci-answer-state--${variante}`}>{texto}</span>;
};

const MAXIMO_LOGO_ORIGINAL = 5 * 1024 * 1024;
const ANCHO_LOGO = 260;
const ALTO_LOGO = 100;

const leerArchivoComoDataUrl = (archivo) => new Promise((resolve, reject) => {
  const lector = new FileReader();
  lector.onload = () => resolve(lector.result);
  lector.onerror = () => reject(new Error("No fue posible leer la imagen seleccionada."));
  lector.readAsDataURL(archivo);
});

const cargarImagen = (origen) => new Promise((resolve, reject) => {
  const imagen = new Image();
  imagen.onload = () => resolve(imagen);
  imagen.onerror = () => reject(new Error("El archivo seleccionado no contiene una imagen PNG válida."));
  imagen.src = origen;
});

const prepararLogo = async (archivo) => {
  if (!archivo || archivo.type !== "image/png") {
    throw new Error("Selecciona una imagen en formato PNG.");
  }
  if (archivo.size > MAXIMO_LOGO_ORIGINAL) {
    throw new Error("El archivo no puede superar 5 MB.");
  }
  const original = await leerArchivoComoDataUrl(archivo);
  const imagen = await cargarImagen(original);
  const escala = Math.min(1, ANCHO_LOGO / imagen.naturalWidth, ALTO_LOGO / imagen.naturalHeight);
  const ancho = Math.max(1, Math.round(imagen.naturalWidth * escala));
  const alto = Math.max(1, Math.round(imagen.naturalHeight * escala));
  const lienzo = document.createElement("canvas");
  lienzo.width = ancho;
  lienzo.height = alto;
  const contexto = lienzo.getContext("2d");
  if (!contexto) throw new Error("El navegador no pudo preparar la vista previa del logo.");
  contexto.clearRect(0, 0, ancho, alto);
  contexto.drawImage(imagen, 0, 0, ancho, alto);
  return lienzo.toDataURL("image/png");
};

const LogoCompaniaEditor = ({ elemento, valor, logo, disabled, labelledBy, onChange }) => {
  const [procesando, setProcesando] = useState(false);
  const [error, setError] = useState("");
  const seleccionadas = Array.isArray(valor?.selectedOptionIds) ? valor.selectedOptionIds : [];
  const seleccionadoCambiar = seleccionadas.includes("EMP-07.CAMBIAR");
  const logoSeleccionado = valor?.logoDataUrl || null;
  const vistaPrevia = logoSeleccionado || logo;

  const seleccionarOpcion = (codigo) => {
    const nuevoValor = { ...(valor || {}), selectedOptionIds: [codigo] };
    if (codigo !== "EMP-07.CAMBIAR") {
      delete nuevoValor.logoDataUrl;
      delete nuevoValor.logoFileName;
      setError("");
    }
    onChange(nuevoValor);
  };

  const seleccionarArchivo = async (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = "";
    if (!archivo) return;
    setProcesando(true);
    setError("");
    try {
      const dataUrl = await prepararLogo(archivo);
      onChange({
        ...(valor || {}),
        selectedOptionIds: ["EMP-07.CAMBIAR"],
        logoDataUrl: dataUrl,
        logoFileName: archivo.name,
      });
    } catch (excepcion) {
      setError(excepcion?.message || "No fue posible preparar el logo.");
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="ci-logo-editor">
      <div className={`ci-logo-picker${seleccionadoCambiar ? " is-enabled" : ""}`}>
        <div className="ci-logo-picker__media">
          {vistaPrevia ? (
            <img src={vistaPrevia} alt={logoSeleccionado ? "Vista previa del nuevo logo" : "Logo actual de la empresa"} />
          ) : (
            <span className="ci-logo-picker__placeholder" aria-hidden="true">▧</span>
          )}
        </div>
        <div className="ci-logo-picker__copy">
          <strong>{logoSeleccionado ? "Nuevo logo listo" : logo ? "Logo registrado actualmente" : "Aún no has agregado un logo"}</strong>
          <span>{logoSeleccionado
            ? "Se registrará cuando finalices y apliques la configuración."
            : "Formato PNG. Citrus lo ajustará a un máximo de 260 × 100 px sin deformarlo."}</span>
          {valor?.logoFileName && <small>{valor.logoFileName}</small>}
        </div>
      </div>

      <div className="ci-logo-options" role="radiogroup" aria-labelledby={labelledBy}>
        {(elemento.Opciones || []).map((opcion) => {
          const seleccionada = seleccionadas.includes(opcion.Codigo);
          const id = `${elemento.Codigo}-${opcion.Codigo}`;
          const esCambiar = opcion.Codigo === "EMP-07.CAMBIAR";
          return (
            <div className={`ci-logo-option-row${esCambiar && seleccionada ? " is-active" : ""}`} key={opcion.Codigo}>
              <label className={`ci-option-card${seleccionada ? " is-selected" : ""}`} htmlFor={id}>
                <input id={id} type="radio" name={elemento.Codigo} disabled={disabled || procesando}
                  checked={seleccionada} onChange={() => seleccionarOpcion(opcion.Codigo)} />
                <span className="ci-option-card__indicator" aria-hidden="true">{seleccionada ? "✓" : ""}</span>
                <span className="ci-option-card__copy"><strong>{opcion.Etiqueta}</strong></span>
              </label>
              {esCambiar && seleccionada && (
                <label className={`ci-logo-picker__button${disabled || procesando ? " is-disabled" : ""}`}>
                  <input type="file" accept="image/png" disabled={disabled || procesando} onChange={seleccionarArchivo} />
                  <span aria-hidden="true">＋</span>
                  {procesando ? "Preparando…" : logoSeleccionado ? "Elegir otro logo" : logo ? "Cambiar logo" : "Agregar logo"}
                </label>
              )}
            </div>
          );
        })}
      </div>
      {error && <Alert variant="warning" className="ci-inline-alert" role="alert">{error}</Alert>}
    </div>
  );
};

const estaVacio = (valor) => {
  if (valor === null || valor === undefined || valor === "") return true;
  if (Array.isArray(valor)) return valor.length === 0;
  if (typeof valor === "object") return Object.values(valor).every(estaVacio);
  return false;
};

const respuestaVacia = (elemento, valor) => {
  if (["SINGLE_CHOICE", "MULTIPLE_CHOICE", "SINGLE_CHOICE_WITH_NOTE"].includes(elemento.TipoRespuesta)) {
    if (elemento.Codigo === "EMP-07"
      && valor?.selectedOptionIds?.includes("EMP-07.CAMBIAR")) return !valor?.logoDataUrl;
    return !Array.isArray(valor?.selectedOptionIds) || valor.selectedOptionIds.length === 0;
  }
  if (["FORM", "REPEATABLE_FORM"].includes(elemento.TipoRespuesta)) {
    return estaVacio(valor?.fields);
  }
  return estaVacio(valor);
};

const requiereTasaInicial = (elemento) =>
  elemento?.requiresInitialRateForNewOptions === true ||
  elemento?.RequiresInitialRateForNewOptions === true ||
  elemento?.RequiereTasaInicialParaOpcionesNuevas === true;

const opcionesActualesDe = (elemento, valorActual) => {
  const contenido = contenidoRelacionadoCompatible(elemento, valorActual);
  return new Set(Array.isArray(contenido?.selectedOptionIds) ? contenido.selectedOptionIds : []);
};

const validarTasasIniciales = (elemento, valor, valorActual) => {
  if (!requiereTasaInicial(elemento)) return "";
  const actuales = opcionesActualesDe(elemento, valorActual);
  const seleccionadas = Array.isArray(valor?.selectedOptionIds) ? valor.selectedOptionIds : [];
  for (const codigo of seleccionadas) {
    if (actuales.has(codigo)) continue;
    const tasaCruda = valor?.optionDetails?.[codigo]?.initialRate;
    const tasa = Number(String(tasaCruda ?? "").replace(",", "."));
    if (!Number.isFinite(tasa) || tasa <= 0) {
      const etiqueta = (elemento.Opciones || []).find((opcion) => opcion.Codigo === codigo)?.Etiqueta || "la moneda seleccionada";
      return `Indica una tasa inicial mayor que cero para ${etiqueta}.`;
    }
  }
  return "";
};

const contenidoParaEstado = (elemento, valor, estadoRespuesta) => {
  if (estadoRespuesta === ESTADOS_RESPUESTA.OMITIDA ||
    estadoRespuesta === ESTADOS_RESPUESTA.SIN_RESPONDER) {
    return null;
  }
  if (estadoRespuesta === ESTADOS_RESPUESTA.PARA_DESPUES && respuestaVacia(elemento, valor)) {
    return null;
  }
  return serializarRespuesta(valor);
};

const tipoHtml = (tipoEntrada) => {
  const tipo = String(tipoEntrada || "").toUpperCase();
  if (tipo.includes("DATE") || tipo.includes("FECHA")) return "date";
  if (tipo.includes("EMAIL") || tipo.includes("CORREO")) return "email";
  return "text";
};

const esCampoTelefono = (campo) => /TELEFONO/.test(String(campo?.Codigo || "").toUpperCase());
const esCampoCorreo = (campo) => /CORREO|EMAIL/.test(String(campo?.Codigo || "").toUpperCase());
const esCampoNumerico = (campo) => String(campo?.ControlEntrada || "").toUpperCase() === "NUMBER";
const CAMPOS_IDENTIFICADOR_NUMERICO = new Set(["EMP-03.NUMERO_FISCAL", "BAN-02.NUMERO_CUENTA"]);
const esCampoIdentificadorNumerico = (campo) => CAMPOS_IDENTIFICADOR_NUMERICO.has(String(campo?.Codigo || "").toUpperCase());

const formatearTelefono = (valor) => {
  const digitos = String(valor ?? "").replace(/\D/g, "").slice(0, 10);
  if (digitos.length <= 3) return digitos;
  if (digitos.length <= 6) return `${digitos.slice(0, 3)}-${digitos.slice(3)}`;
  return `${digitos.slice(0, 3)}-${digitos.slice(3, 6)}-${digitos.slice(6)}`;
};

const normalizarNumeroEntrada = (valor) => {
  const limpio = String(valor ?? "").replace(/,/g, ".").replace(/[^0-9.]/g, "");
  const partes = limpio.split(".");
  return partes.length <= 1 ? partes[0] : `${partes.shift()}.${partes.join("")}`;
};
const normalizarSoloDigitos = (valor) => String(valor ?? "").replace(/\D/g, "");

const normalizarCorreoEntrada = (valor) => String(valor ?? "").replace(/\s/g, "");
const correoValido = (valor) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(valor || ""));

const validarFormatoCampos = (elemento, valor) => {
  const valores = valor?.fields || {};
  if (elemento.Codigo === "MOD-01A") {
    const requeridos = [
      ["MOD-01A.FONDO_PERMANENTE", "el fondo permanente"],
      ["MOD-01A.LIMITE_PDPS", "el límite diario por suplidor"],
      ["MOD-01A.LIMITE_MPR", "el límite máximo para reposición"],
      ["MOD-01A.SUPERVISOR", "el supervisor"],
      ["MOD-01A.CUENTA_BANCO", "la cuenta bancaria para reposiciones"],
      ["MOD-01A.CUENTA_CONTABLE", "la cuenta contable principal"],
    ];
    const incompleto = requeridos.find(([codigo]) => valores[codigo] === null
      || valores[codigo] === undefined || String(valores[codigo]).trim() === "");
    if (incompleto) return `Completa ${incompleto[1]} de Caja Chica.`;
    const fondo = Number(String(valores["MOD-01A.FONDO_PERMANENTE"]).replace(",", "."));
    if (!Number.isFinite(fondo) || fondo <= 0) return "El fondo permanente debe ser mayor que cero.";
    for (const [codigo, etiqueta] of [
      ["MOD-01A.LIMITE_PDPS", "El límite diario por suplidor"],
      ["MOD-01A.LIMITE_MPR", "El límite máximo para reposición"],
    ]) {
      const porcentaje = Number(String(valores[codigo]).replace(",", "."));
      if (!Number.isFinite(porcentaje) || porcentaje <= 0 || porcentaje > 100) {
        return `${etiqueta} debe ser mayor que cero y no exceder 100%.`;
      }
    }
  }
  for (const campo of elemento.Opciones || []) {
    const contenido = valores[campo.Codigo];
    if (contenido === null || contenido === undefined || String(contenido).trim() === "") continue;
    if (esCampoTelefono(campo)) {
      const telefono = String(contenido);
      if (!/^[0-9\s().-]+$/.test(telefono) || (telefono.match(/\d/g) || []).length !== 10) {
        return `${campo.Etiqueta || "El teléfono"} debe tener el formato ###-###-####.`;
      }
    }
    if (esCampoCorreo(campo) && !correoValido(contenido)) {
      return `${campo.Etiqueta || "El correo electrónico"} debe tener un formato válido, por ejemplo nombre@empresa.com.`;
    }
    if (esCampoNumerico(campo) && !/^\d+(?:\.\d+)?$/.test(String(contenido))) {
      return `${campo.Etiqueta || "El campo"} solo admite números.`;
    }
    if (esCampoIdentificadorNumerico(campo)
      && (!/^[0-9\s.-]+$/.test(String(contenido)) || !/\d/.test(String(contenido)))) {
      return `${campo.Etiqueta || "El campo"} solo admite números.`;
    }
  }
  return "";
};

const seleccionarContenidoNumerico = (evento) => {
  const control = evento?.currentTarget;
  if (!control || typeof control.select !== "function") return;
  const seleccionar = () => {
    if (control.isConnected !== false && !control.disabled) control.select();
  };
  seleccionar();
  // Algunos navegadores vuelven a colocar el cursor al terminar el clic.
  // Repetir la selección en el siguiente frame garantiza el reemplazo completo.
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(seleccionar);
  }
};

const conservarSeleccionNumerica = (evento) => {
  evento.preventDefault();
  seleccionarContenidoNumerico(evento);
};

const CamposFormulario = ({ elemento, valor, onChange, disabled, onValidacionUbicacion, idPrefix = elemento.Codigo }) => {
  if (elemento.Codigo === "EMP-05") return <UbicacionConfiguracionInicial valor={valor} onChange={onChange} disabled={disabled} onValidacion={onValidacionUbicacion} />;
  const campos = elemento.Opciones || [];
  const valores = valor?.fields || {};
  const esImpresionTiendas = elemento.Codigo === "VEN-09";
  const formatoImpresion = valores["VEN-09.FORMATO"];
  const camposPrincipalesImpresion = new Set([
    "VEN-09.FORMATO", "VEN-09.TIPO_IMPRESORA", "VEN-09.TAMANO_LETRA", "VEN-09.DECIMALES",
    "VEN-09.TIPO_ARCHIVO_RECIBO", "VEN-09.TAMANO_LETRA_RECIBO",
  ]);
  const usaDepreciacionesFiscales = valores["MOD-02A.DEPRECIACIONES_FISCALES"] === true;

  const renderCampo = (campo) => {
    if (campo.Codigo === "BAN-02.INACTIVAR") return null;
    if (campo.Codigo === "MOD-02A.AJUSTE_FISCAL_AUTOMATICO" && !usaDepreciacionesFiscales) return null;
    const precio = /^VEN-07\.PRECIO([2-5])_LABEL$/.exec(campo.Codigo);
    if (precio && Number(precio[1]) > Number(valores["VEN-07.CANTIDAD_PRECIOS"] || 0)) return null;
    if (esImpresionTiendas && campo.Codigo === "VEN-09.TIPO_IMPRESORA" && formatoImpresion === "P") return null;
    if (esImpresionTiendas
      && ["VEN-09.TIPO_ARCHIVO_RECIBO", "VEN-09.TAMANO_LETRA_RECIBO"].includes(campo.Codigo)
      && formatoImpresion !== "P") return null;
    const control = String(campo.ControlEntrada || "TEXT").toUpperCase();
    const campoTelefono = esCampoTelefono(campo);
    const campoCorreo = esCampoCorreo(campo);
    const campoNumerico = esCampoNumerico(campo);
    const campoIdentificadorNumerico = esCampoIdentificadorNumerico(campo);
    if (control === "HIDDEN") return null;
    // Presentar también respuestas antiguas con código ISO sin sobrescribir lo guardado.
    const valorOriginal = campo.Codigo === "EMP-05.PAIS" && /^(DO|RD)$/i.test(String(valores[campo.Codigo] ?? "").trim())
      ? "República Dominicana" : valores[campo.Codigo] ?? "";
    const valorCampo = campoTelefono ? formatearTelefono(valorOriginal)
      : campoIdentificadorNumerico ? normalizarSoloDigitos(valorOriginal)
      : valorOriginal;
    const actualizarCampo = (nuevoValor) => {
      const nuevosValores = { ...valores, [campo.Codigo]: nuevoValor };
      if (campo.Codigo === "MOD-02A.DEPRECIACIONES_FISCALES" && nuevoValor !== true) {
        nuevosValores["MOD-02A.AJUSTE_FISCAL_AUTOMATICO"] = false;
      }
      onChange({ ...(valor || {}), fields: nuevosValores });
    };

    if (control === "CHECKBOX") {
      return (
        <Form.Group className="ci-field ci-field--checkbox" key={campo.Codigo} controlId={`${idPrefix}-${campo.Codigo}`}>
          <Form.Check
            type="checkbox"
            label={campo.Etiqueta}
            checked={valores[campo.Codigo] === true}
            disabled={disabled}
            onChange={(evento) => actualizarCampo(evento.target.checked)}
          />
          {campo.Descripcion && <Form.Text>{campo.Descripcion}</Form.Text>}
        </Form.Group>
      );
    }

    return (
      <Form.Group className="ci-field" key={campo.Codigo} controlId={`${idPrefix}-${campo.Codigo}`}>
        <Form.Label>{campo.Etiqueta}</Form.Label>
        {control === "SELECT" ? (
          <Form.Select
            value={valorCampo}
            disabled={disabled}
            onChange={(evento) => actualizarCampo(evento.target.value)}
          >
            <option value="">Selecciona una opción</option>
            {(campo.ValoresPermitidos || []).map((opcion) => (
              <option value={opcion.Valor} key={opcion.Valor}>{opcion.Etiqueta}</option>
            ))}
          </Form.Select>
        ) : (
          <Form.Control
            type={campoTelefono ? "tel" : campoCorreo ? "email" : tipoHtml(campo.TipoEntrada)}
            placeholder={campoTelefono ? "###-###-####" : pistaCampo(campo.Codigo)}
            inputMode={campoTelefono || campoIdentificadorNumerico ? "numeric" : campoCorreo ? "email" : campoNumerico ? "decimal" : undefined}
            pattern={campoTelefono ? "[0-9]{3}-[0-9]{3}-[0-9]{4}" : campoCorreo ? "[^\\s@]+@[^\\s@]+\\.[^\\s@]+" : campoNumerico ? "[0-9]+(?:\\.[0-9]+)?" : campoIdentificadorNumerico ? "[0-9]+" : undefined}
            maxLength={campoTelefono ? 12 : campoCorreo ? 255 : undefined}
            autoComplete={campoTelefono ? "tel" : campoCorreo ? "email" : undefined}
            value={valorCampo}
            disabled={disabled}
            onFocus={campoNumerico ? seleccionarContenidoNumerico : undefined}
            onClick={campoNumerico ? seleccionarContenidoNumerico : undefined}
            onMouseUp={campoNumerico ? conservarSeleccionNumerica : undefined}
            onChange={(evento) => actualizarCampo(campoTelefono
              ? formatearTelefono(evento.target.value)
              : campoCorreo
                ? normalizarCorreoEntrada(evento.target.value)
                : campoNumerico
                  ? normalizarNumeroEntrada(evento.target.value)
                  : campoIdentificadorNumerico
                    ? normalizarSoloDigitos(evento.target.value)
                    : evento.target.value)}
          />
        )}
        {campo.Descripcion && <Form.Text>{campo.Descripcion}</Form.Text>}
      </Form.Group>
    );
  };

  if (esImpresionTiendas) {
    const principales = campos.filter((campo) => camposPrincipalesImpresion.has(campo.Codigo));
    const adicionales = campos.filter((campo) => !camposPrincipalesImpresion.has(campo.Codigo));
    return <>
      <div className="ci-form-grid">{principales.map(renderCampo)}</div>
      <details className="ci-print-options">
        <summary>Opciones adicionales de la factura <span>Personaliza la información que se mostrará al imprimir</span></summary>
        <div className="ci-form-grid">{adicionales.map(renderCampo)}</div>
      </details>
    </>;
  }

  return <div className="ci-form-grid">{campos.map(renderCampo)}</div>;
};

const ControlRespuesta = ({ elemento, valor, valorActual, logoCompania, onChange, disabled, labelledBy, onValidacionUbicacion }) => {
  const tipo = elemento.TipoRespuesta;
  const opciones = elemento.Opciones || [];

  if (tipo === "SINGLE_CHOICE" || tipo === "SINGLE_CHOICE_WITH_NOTE") {
    const seleccionadas = Array.isArray(valor?.selectedOptionIds) ? valor.selectedOptionIds : [];
    const visibles = opcionesVisibles(elemento, seleccionadas);
    if (elemento.Codigo === "EMP-07") {
      return <LogoCompaniaEditor elemento={elemento} valor={valor} logo={logoCompania}
        onChange={onChange} disabled={disabled} labelledBy={labelledBy} />;
    }
    return (
      <>
        {elemento.Codigo === "SED-09" && (
          <p className="ci-selection-hint ci-selection-hint--tag">Responde Sí solo si estás seguro.</p>
        )}
        <div className={`ci-options${visibles.every(opcion => opcion.Etiqueta?.length < 60) ? ' ci-options--columns' : ''}`} role="radiogroup" aria-labelledby={labelledBy}>
          {visibles.map((opcion) => {
            const seleccionada = seleccionadas.includes(opcion.Codigo);
            const id = `${elemento.Codigo}-${opcion.Codigo}`;
            return (
              <label className={`ci-option-card${seleccionada ? " is-selected" : ""}`} htmlFor={id} key={opcion.Codigo}>
                <input
                  id={id}
                  type="radio"
                  name={elemento.Codigo}
                  disabled={disabled}
                  checked={seleccionada}
                  onChange={() => onChange({
                    ...(valor || {}),
                    selectedOptionIds: [opcion.Codigo],
                  })}
                />
                <span className="ci-option-card__indicator" aria-hidden="true">{seleccionada ? "✓" : ""}</span>
                <span className="ci-option-card__copy">
                  <strong>{opcion.Etiqueta}</strong>
                  {opcion.Descripcion && <small>{opcion.Descripcion}</small>}
                </span>
              </label>
            );
          })}
        </div>
        {tipo === "SINGLE_CHOICE_WITH_NOTE" && (
          <Form.Group className="mt-3" controlId={`${elemento.Codigo}-nota`}>
            <Form.Label>Detalle</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              disabled={disabled}
              value={valor?.note || ""}
              onChange={(evento) => onChange({ ...(valor || {}), note: evento.target.value })}
            />
          </Form.Group>
        )}
      </>
    );
  }

  if (tipo === "MULTIPLE_CHOICE") {
    const seleccionadas = Array.isArray(valor?.selectedOptionIds) ? valor.selectedOptionIds : [];
    const opcionesActuales = opcionesActualesDe(elemento, valorActual);
    const solicitaTasa = requiereTasaInicial(elemento);
    return (
      <><p className="ci-selection-hint ci-selection-hint--tag">Puedes seleccionar varias opciones.</p>
      <div className={`ci-options${!solicitaTasa && !elemento.CamposDetalle?.length ? ' ci-options--columns' : ''}`} role="group" aria-labelledby={labelledBy}>
        {opciones.map((opcion) => {
          const codigo = opcion.Codigo;
          const seleccionada = seleccionadas.includes(codigo);
          const id = `${elemento.Codigo}-${opcion.Codigo}`;
          const requiereDetalle = solicitaTasa && seleccionada && !opcionesActuales.has(codigo);
          const detalles = valor?.optionDetails || {};
          return (
            <div className={`ci-option-with-detail${seleccionada ? " is-selected" : ""}`} key={opcion.Codigo}>
              <label className={`ci-option-card${seleccionada ? " is-selected" : ""}`} htmlFor={id}>
                <input
                  id={id}
                  type="checkbox"
                  disabled={disabled}
                  checked={seleccionada}
                  onChange={(evento) => {
                    const nuevosDetalles = { ...detalles };
                    if (!evento.target.checked) delete nuevosDetalles[codigo];
                    const nuevoValor = {
                      ...(valor || {}),
                      selectedOptionIds: evento.target.checked
                        ? [...seleccionadas, codigo]
                        : seleccionadas.filter((item) => item !== codigo),
                    };
                    if (solicitaTasa || elemento.CamposDetalle?.length) nuevoValor.optionDetails = nuevosDetalles;
                    else delete nuevoValor.optionDetails;
                    if ((elemento.Codigo === "FIS-04" || elemento.Codigo === "FIS-05") && evento.target.checked) {
                      const ninguno = `${elemento.Codigo}.NINGUNO`;
                      nuevoValor.selectedOptionIds = codigo === ninguno ? [ninguno] : nuevoValor.selectedOptionIds.filter(c => c !== ninguno);
                      if (codigo === ninguno) nuevoValor.optionDetails = {};
                    }
                    onChange(nuevoValor);
                  }}
                />
                <span className="ci-option-card__indicator ci-option-card__indicator--checkbox" aria-hidden="true">{seleccionada ? "✓" : ""}</span>
                <span className="ci-option-card__copy">
                  <strong>{opcion.Etiqueta}</strong>
                  {opcion.Descripcion && <small>{opcion.Descripcion}</small>}
                </span>
              </label>
              {seleccionada && elemento.CamposDetalle?.length > 0
                && !codigo.endsWith(".NINGUNO")
                && !/^FIS-0[45]\.(ISC|PROPINA_LEGAL)$/.test(codigo)
                && (!(elemento.Codigo === "FIS-04" || elemento.Codigo === "FIS-05") || !opcionesActuales.has(codigo)) && (
                <div className="ci-option-rate">
                  <p>Completa los datos de esta opción.</p>
                  <CamposFormulario elemento={{ Codigo: id, Opciones: elemento.CamposDetalle }}
                    valor={{ fields: detalles[codigo] || {} }} disabled={disabled}
                    onChange={(detalle) => onChange({ ...valor, optionDetails: { ...detalles, [codigo]: detalle.fields } })} />
                </div>
              )}
              {requiereDetalle && (
                <Form.Group className="ci-option-rate" controlId={`${id}-tasa`}>
                  <Form.Label>Tasa inicial respecto al Peso Dominicano</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.000001"
                    step="0.000001"
                    inputMode="decimal"
                    required
                    disabled={disabled}
                    value={detalles[codigo]?.initialRate ?? ""}
                    onFocus={seleccionarContenidoNumerico}
                    onClick={seleccionarContenidoNumerico}
                    onMouseUp={conservarSeleccionNumerica}
                    onChange={(evento) => onChange({
                      ...(valor || {}),
                      selectedOptionIds: seleccionadas,
                      optionDetails: {
                        ...detalles,
                        [codigo]: { initialRate: evento.target.value },
                      },
                    })}
                  />
                  <Form.Text>Indica cuántos pesos dominicanos equivalen a una unidad de esta moneda.</Form.Text>
                </Form.Group>
              )}
            </div>
          );
        })}
      </div></>
    );
  }

  if (tipo === "FORM" || tipo === "REPEATABLE_FORM") {
    return <CamposFormulario elemento={elemento} valor={valor || { fields: {} }} onChange={onChange} disabled={disabled} onValidacionUbicacion={onValidacionUbicacion} />;
  }

  if (tipo === "NONE") return null;

  return (
    <Form.Control
      className="ci-long-answer"
      as="textarea"
      aria-labelledby={labelledBy}
      rows={3}
      value={valor ?? ""}
      disabled={disabled}
      onChange={(evento) => onChange(evento.target.value)}
    />
  );
};

const AccionesRespuesta = ({
  elemento,
  ocupado,
  continuarDeshabilitado,
  anteriorDeshabilitado,
  etiquetaContinuar,
  onAnterior,
  onContinuar,
  onDespues,
  onOmitir,
}) => (
  <div className="ci-question-footer">
    <button type="button" className="ci-footer-link" disabled={ocupado || anteriorDeshabilitado} onClick={onAnterior}>← Anterior</button>
    <div className="ci-question-footer__actions">
      {elemento.PuedeOmitirse && (
        <Button variant="link" className="ci-button-link" disabled={ocupado} onClick={onOmitir}>Omitir</Button>
      )}
      <Button className="ci-button ci-button--secondary" variant="outline-secondary" disabled={ocupado} onClick={onDespues}>Responder después</Button>
      <Button className="ci-button ci-button--continue" disabled={ocupado || continuarDeshabilitado} onClick={onContinuar}>
        {ocupado && <Spinner animation="border" size="sm" className="me-2" />}
        {etiquetaContinuar} <span aria-hidden="true">→</span>
      </Button>
    </div>
  </div>
);

const ElementoSimple = ({
  elemento,
  respuesta,
  valorActual,
  sugerencia,
  guardando,
  onGuardar,
  onGuardarYContinuar,
  onActivar,
  ubicacion,
  onAnterior,
  onContinuar,
  etiquetaContinuar,
  anteriorDeshabilitado,
}) => {
  const tituloRef = useRef(null);
  const fuentePrellenado = respuesta ? null : (sugerencia || valorActual);
  const logoCompania = elemento.Codigo === "EMP-07"
    ? obtenerPrimero(valorActual || sugerencia, ["VistaPreviaUrl", "vistaPreviaUrl"], null)
    : null;
  const valorInicial = respuesta
    ? contenidoCompatible(elemento, respuesta)
    : contenidoRelacionadoCompatible(elemento, fuentePrellenado);
  const [valor, setValor] = useState(valorInicial);
  const [validacion, setValidacion] = useState("");
  const guardadoEnCursoRef = useRef(false);
  const [guardadoLocal, setGuardadoLocal] = useState(false);
  const [validacionUbicacion, setValidacionUbicacion] = useState(elemento.Codigo === "EMP-05" ? "Espera a que se carguen las ubicaciones." : "");
  const estado = estadoDe(respuesta);
  const hayCambiosSinGuardar = JSON.stringify(valor) !== JSON.stringify(valorInicial);
  const requiereReemplazarRespuesta = respuestaContieneOpcionesRetiradas(elemento, respuesta)
    && respuestaVacia(elemento, valor);

  useEffect(() => {
    setValor(respuesta
      ? contenidoCompatible(elemento, respuesta)
      : contenidoRelacionadoCompatible(elemento, fuentePrellenado));
  }, [elemento, fuentePrellenado, respuesta]);

  useEffect(() => {
    tituloRef.current?.focus();
  }, [elemento.Codigo]);

  const guardarEstado = async (nuevoEstado, avanzarDespues = false) => {
    if (guardando || guardadoEnCursoRef.current) return;
    if (elemento.Codigo === "EMP-05" && nuevoEstado === ESTADOS_RESPUESTA.RESPONDIDA && validacionUbicacion) {
      setValidacion(validacionUbicacion);
      return;
    }
    if (nuevoEstado === ESTADOS_RESPUESTA.RESPONDIDA && respuestaVacia(elemento, valor)) {
      setValidacion("Completa esta respuesta antes de guardarla.");
      return;
    }
    if (nuevoEstado === ESTADOS_RESPUESTA.RESPONDIDA) {
      const mensajeFormato = validarFormatoCampos(elemento, valor);
      if (mensajeFormato) {
        setValidacion(mensajeFormato);
        return;
      }
      const mensajeTasa = validarTasasIniciales(elemento, valor, valorActual);
      if (mensajeTasa) {
        setValidacion(mensajeTasa);
        return;
      }
    }
    setValidacion("");
    guardadoEnCursoRef.current = true;
    setGuardadoLocal(true);
    try {
      const valorPersistido = contenidoCompatible(elemento, respuesta);
      const sinCambios = Boolean(respuesta) && JSON.stringify(valor) === JSON.stringify(valorPersistido);
      if (avanzarDespues && nuevoEstado === estado && sinCambios) {
        await onContinuar();
        return;
      }
      const solicitud = {
        codigoElemento: elemento.Codigo,
        estadoRespuesta: nuevoEstado,
        contenidoRespuestaJson: contenidoParaEstado(elemento, valor, nuevoEstado),
        codigoEtapaActual: ubicacion.codigoEtapaActual,
        codigoElementoActual: elemento.Codigo,
        codigoOcurrenciaActual: CODIGO_OCURRENCIA_UNICA,
      };
      if (avanzarDespues) await onGuardarYContinuar(solicitud);
      else await onGuardar(solicitud);
    } catch (_error) {
      setValidacion("No pudimos guardar esta respuesta. Tus cambios siguen aquí; inténtalo de nuevo.");
    } finally {
      guardadoEnCursoRef.current = false;
      setGuardadoLocal(false);
    }
  };

  return (
    <article
      className="ci-question"
      data-ci-question
      data-ci-dirty={hayCambiosSinGuardar}
      id={`ci-element-${elemento.Codigo}`}
      onFocus={(evento) => {
        if (!evento.currentTarget.contains(evento.relatedTarget)) onActivar(elemento.Codigo);
      }}
    >
      <div className="ci-question__heading">
        <div>
          <h1 className="ci-question__title" id={`ci-title-${elemento.Codigo}`} ref={tituloRef} tabIndex="-1">{elemento.Titulo}</h1>
        </div>
        {guardando || guardadoLocal
          ? <span className="ci-answer-state ci-answer-state--warning" role="status">Guardando…</span>
          : requiereReemplazarRespuesta
          ? <span className="ci-answer-state ci-answer-state--warning">Requiere revisión</span>
          : hayCambiosSinGuardar && respuesta
          ? <span className="ci-answer-state ci-answer-state--warning">Cambios sin guardar</span>
          : <EstadoRespuesta estado={estado} />}
      </div>
      {elemento.Ayuda && <p className="ci-question__help">{elemento.Ayuda}</p>}
      {requiereReemplazarRespuesta && (
        <Alert variant="warning" className="ci-inline-alert">
          Una opción elegida anteriormente ya no está disponible. Selecciona una de las alternativas vigentes para continuar.
        </Alert>
      )}
      <div className="ci-question__control">
        <ControlRespuesta
          onValidacionUbicacion={setValidacionUbicacion}
          elemento={elemento}
          valor={valor}
          valorActual={valorActual}
          logoCompania={logoCompania}
          onChange={setValor}
          disabled={guardando || guardadoLocal}
          labelledBy={`ci-title-${elemento.Codigo}`}
        />
      </div>
      {validacion && <Alert variant="warning" className="ci-inline-alert">{validacion}</Alert>}
      <AccionesRespuesta
        elemento={elemento}
        ocupado={guardando || guardadoLocal}
        continuarDeshabilitado={respuestaVacia(elemento, valor) || requiereReemplazarRespuesta}
        anteriorDeshabilitado={anteriorDeshabilitado}
        etiquetaContinuar={etiquetaContinuar}
        onAnterior={onAnterior}
        onContinuar={() => guardarEstado(ESTADOS_RESPUESTA.RESPONDIDA, true)}
        onDespues={() => guardarEstado(ESTADOS_RESPUESTA.PARA_DESPUES, true)}
        onOmitir={() => guardarEstado(ESTADOS_RESPUESTA.OMITIDA, true)}
      />
    </article>
  );
};

const nuevaOcurrencia = () => {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const crearOcurrenciaLocal = (creadaAutomaticamente = false) => ({
  codigo: nuevaOcurrencia(),
  valor: { fields: {} },
  estado: ESTADOS_RESPUESTA.SIN_RESPONDER,
  persistida: false,
  sucia: true,
  guardando: false,
  origenCitrus: false,
  creadaAutomaticamente,
});

const codigoOcurrenciaSugerida = (relacionado, indice) => {
  const codigo = obtenerPrimero(relacionado, ["CodigoOcurrencia", "codigoOcurrencia"], null);
  if (codigo && codigo !== CODIGO_OCURRENCIA_UNICA) return codigo;
  const identidad = obtenerPrimero(
    relacionado,
    ["IdRegistro", "idRegistro", "RegistroId", "registroId", "Id", "id"],
    indice + 1
  );
  const segura = String(identidad).replace(/[^A-Za-z0-9_-]/g, "-").slice(0, 32) || String(indice + 1);
  return `CITRUS-${segura}`;
};

const reconciliarOcurrencias = (actuales, recibidas, guardandoCodigo) => {
  const porCodigo = new Map(recibidas.map((recibida) => [recibida.codigo, recibida]));
  const hayRecibidas = recibidas.length > 0;
  const incluidas = new Set();
  const resultado = actuales.flatMap((local) => {
    const recibida = porCodigo.get(local.codigo);
    if (!recibida) {
      // Si los datos autoritativos llegan después del primer render, la fila
      // automática intacta deja paso a esos registros y no se duplica.
      if (local.creadaAutomaticamente && hayRecibidas) return [];
      // Una fila nueva todavía no existe en la respuesta del servidor. Se
      // conserva en su posición local hasta que el usuario decida guardarla.
      return local.persistida ? [] : [local];
    }
    incluidas.add(local.codigo);
    // Una actualización general no debe mover ni volver pendiente la fila que
    // se está guardando, ni descartar la edición pendiente de otra fila.
    if (local.guardando) return [local];
    if (local.persistida && (!recibida.persistida || local.version > recibida.version)) return [local];
    return [local.sucia && local.codigo !== guardandoCodigo ? local : recibida];
  });
  recibidas.forEach((recibida) => {
    if (!incluidas.has(recibida.codigo)) resultado.push(recibida);
  });
  return resultado;
};

const ElementoRepetible = ({
  elemento,
  respuestas,
  valoresActuales,
  sugerencias,
  guardando,
  onGuardar,
  onGuardarVariasYContinuar,
  onActivar,
  ubicacion,
  onAnterior,
  onContinuar,
  etiquetaContinuar,
  anteriorDeshabilitado,
}) => {
  const tituloRef = useRef(null);
  const respuestasElemento = useMemo(() => respuestas
    .filter((respuesta) => obtenerPrimero(respuesta, ["CodigoElemento", "codigoElemento"]) === elemento.Codigo),
  [elemento.Codigo, respuestas]);
  const marcadorOmision = useMemo(() => respuestasElemento.find((respuesta) =>
    obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"]) === CODIGO_OCURRENCIA_OMISION_PREGUNTA
  ), [respuestasElemento]);
  const preguntaOmitida = estadoDe(marcadorOmision) === ESTADOS_RESPUESTA.OMITIDA;
  const ocurrenciasOmitidas = useMemo(() => respuestasElemento.filter((respuesta) =>
    obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"]) !== CODIGO_OCURRENCIA_OMISION_PREGUNTA
      && estadoDe(respuesta) === ESTADOS_RESPUESTA.OMITIDA
  ), [respuestasElemento]);
  const relacionadosActuales = useMemo(() => (valoresActuales || []).filter((item) =>
    item.CodigoElemento === elemento.Codigo
  ), [elemento.Codigo, valoresActuales]);
  const relacionadosSugeridos = useMemo(() => (sugerencias || []).filter((item) =>
    item.CodigoElemento === elemento.Codigo
  ), [elemento.Codigo, sugerencias]);
  const fuentesRelacionadas = relacionadosSugeridos.length > 0 ? relacionadosSugeridos : relacionadosActuales;
  const prellenados = useMemo(() => {
    const guardadas = new Set(respuestasElemento.map((r) => obtenerPrimero(r, ["CodigoOcurrencia", "codigoOcurrencia"])));
    const cuentasChequeGuardadas = elemento.Codigo === "BAN-04"
      ? new Set(respuestasElemento.map((respuesta) => contenidoDe(respuesta)?.fields?.["BAN-04.CUENTA"]).filter(Boolean).map(String))
      : new Set();
    return fuentesRelacionadas.map((item, indice) => ({
      codigo: codigoOcurrenciaSugerida(item, indice),
      valor: contenidoRelacionadoCompatible(elemento, item) || {},
      estado: ESTADOS_RESPUESTA.SIN_RESPONDER,
      persistida: false,
      sucia: true,
      origenCitrus: true,
    })).filter((fila) => !guardadas.has(fila.codigo)
      && (elemento.Codigo !== "BAN-04" || !cuentasChequeGuardadas.has(String(fila.valor?.fields?.["BAN-04.CUENTA"] || ""))));
  }, [elemento, fuentesRelacionadas, respuestasElemento]);
  const existentes = useMemo(() => respuestasElemento
    .filter((respuesta) => {
      const codigo = obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"]);
      return codigo !== CODIGO_OCURRENCIA_OMISION_PREGUNTA
        && (estadoDe(respuesta) !== ESTADOS_RESPUESTA.OMITIDA || String(codigo || "").startsWith("ERP-"));
    })
    .map((respuesta) => {
      const codigo = obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"]);
      const indiceRelacionado = fuentesRelacionadas.findIndex((item, indice) =>
        codigoOcurrenciaSugerida(item, indice) === codigo
      );
      const valorRelacionado = indiceRelacionado >= 0
        ? contenidoRelacionadoCompatible(elemento, fuentesRelacionadas[indiceRelacionado])
        : null;
      return {
        codigo,
        valor: contenidoDe(respuesta) || valorRelacionado || {},
        estado: estadoDe(respuesta),
        version: Number(obtenerPrimero(respuesta, ["VersionRegistroRespuesta", "versionRegistroRespuesta"], 0)),
        persistida: true,
        sucia: false,
        origenCitrus: String(codigo || "").startsWith("ERP-"),
      };
    }), [elemento, fuentesRelacionadas, respuestasElemento]);
  const [ocurrencias, setOcurrencias] = useState(() => {
    const iniciales = [...existentes, ...prellenados];
    if (elemento.Codigo !== "BAN-04" || preguntaOmitida || iniciales.length > 0) return iniciales;
    return [crearOcurrenciaLocal(true)];
  });
  const [validacion, setValidacion] = useState("");
  const [guardandoGrupo, setGuardandoGrupo] = useState(false);
  const guardandoCodigoRef = useRef(null);
  const guardandoGrupoRef = useRef(false);
  const focoPendienteRef = useRef(null);

  const irARegistro = useCallback((codigo, desplazamientoSuave = true) => {
    const fila = document.getElementById(`ci-occurrence-${elemento.Codigo}-${codigo}`);
    fila?.scrollIntoView?.({ behavior: desplazamientoSuave ? "smooth" : "auto", block: "center" });
    fila?.querySelector("input:not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled)")?.focus({ preventScroll: true });
  }, [elemento.Codigo]);

  useEffect(() => {
    setOcurrencias((actuales) => reconciliarOcurrencias(actuales, [...existentes, ...prellenados], guardandoCodigoRef.current));
  }, [existentes, prellenados]);

  useLayoutEffect(() => {
    const codigo = focoPendienteRef.current;
    if (!codigo || !ocurrencias.some((ocurrencia) => ocurrencia.codigo === codigo)) return;
    focoPendienteRef.current = null;
    irARegistro(codigo, false);
  }, [irARegistro, ocurrencias]);

  useEffect(() => {
    tituloRef.current?.focus();
  }, [elemento.Codigo]);

  const maximo = Number(obtenerPrimero(elemento.Repetible, ["MaximoOcurrencias", "maximoOcurrencias", "MaxOccurrences", "maxOccurrences"], 0)) || null;
  const actualizar = (codigo, cambios) => setOcurrencias((actuales) =>
    actuales.map((ocurrencia) => ocurrencia.codigo === codigo
      ? { ...ocurrencia, ...cambios, sucia: true, creadaAutomaticamente: false }
      : ocurrencia)
  );
  const guardarOcurrencia = async (ocurrencia, estadoRespuesta) => {
    if (guardandoCodigoRef.current) return;
    setValidacion("");
    guardandoCodigoRef.current = ocurrencia.codigo;
    setOcurrencias((actuales) => actuales.map((item) =>
      item.codigo === ocurrencia.codigo ? {
        ...item,
        estado: estadoRespuesta,
        sucia: false,
        guardando: true,
      } : item
    ));
    try {
      const carga = await onGuardar({
        codigoElemento: elemento.Codigo,
        codigoOcurrencia: ocurrencia.codigo,
        estadoRespuesta,
        contenidoRespuestaJson: contenidoParaEstado(elemento, ocurrencia.valor, estadoRespuesta),
        codigoEtapaActual: ubicacion.codigoEtapaActual,
        codigoElementoActual: elemento.Codigo,
        codigoOcurrenciaActual: ocurrencia.codigo,
      });
      const confirmada = carga?.respuestas?.find((respuesta) =>
        obtenerPrimero(respuesta, ["CodigoElemento", "codigoElemento"]) === elemento.Codigo &&
        obtenerPrimero(respuesta, ["CodigoOcurrencia", "codigoOcurrencia"]) === ocurrencia.codigo
      );
      setOcurrencias((actuales) => actuales.map((item) =>
        item.codigo === ocurrencia.codigo ? {
          ...item,
          valor: confirmada ? contenidoDe(confirmada) || {} : parsearJsonSeguro(contenidoParaEstado(elemento, ocurrencia.valor, estadoRespuesta), {}) || {},
          estado: confirmada ? estadoDe(confirmada) : estadoRespuesta,
          version: Number(obtenerPrimero(confirmada, ["VersionRegistroRespuesta", "versionRegistroRespuesta"], 0)),
          persistida: true,
          sucia: false,
          guardando: false,
        } : item
      ));
    } catch (_error) {
      setOcurrencias((actuales) => actuales.map((item) =>
        item.codigo === ocurrencia.codigo ? {
          ...item,
          estado: ocurrencia.estado || ESTADOS_RESPUESTA.SIN_RESPONDER,
          persistida: ocurrencia.persistida === true,
          sucia: true,
          guardando: false,
        } : item
      ));
      setValidacion("No pudimos guardar este registro. Tus cambios siguen aquí; inténtalo de nuevo.");
    } finally {
      guardandoCodigoRef.current = null;
    }
  };
  const agregar = () => {
    const nueva = crearOcurrenciaLocal();
    focoPendienteRef.current = nueva.codigo;
    // Todos los formularios repetibles agregan la fila nueva al final. No se
    // crea una respuesta pendiente en el servidor hasta pulsar Guardar.
    setOcurrencias((actuales) => [...actuales, nueva]);
  };
  const eliminar = (ocurrencia) => {
    const esRegistroOperativo = String(ocurrencia.codigo || "").startsWith("ERP-") || ocurrencia.origenCitrus === true;
    if (elemento.Codigo === "BAN-02" && esRegistroOperativo) {
      actualizar(ocurrencia.codigo, { valor: {
        ...ocurrencia.valor,
        fields: {
          ...ocurrencia.valor?.fields,
          "BAN-02.INACTIVAR": ocurrencia.valor?.fields?.["BAN-02.INACTIVAR"] !== true,
        },
      } });
      return;
    }
    if (!ocurrencia.persistida) {
      setOcurrencias((actuales) => actuales.filter((item) => item.codigo !== ocurrencia.codigo));
      return;
    }
    actualizar(ocurrencia.codigo, { eliminada: ocurrencia.eliminada !== true });
  };
  const guardarGrupo = async (estadoGrupo) => {
    if (guardandoGrupoRef.current || guardando) return;
    setValidacion("");

    if (estadoGrupo === ESTADOS_RESPUESTA.RESPONDIDA) {
      const incompleta = ocurrencias.find((ocurrencia) =>
        !ocurrencia.eliminada && respuestaVacia(elemento, ocurrencia.valor)
      );
      if (incompleta) {
        setValidacion(`Completa los datos de ${nombreRegistro(elemento, ocurrencias.indexOf(incompleta)).toLowerCase()} antes de continuar.`);
        irARegistro(incompleta.codigo, false);
        return;
      }
      const formatoInvalido = ocurrencias.find((ocurrencia) =>
        !ocurrencia.eliminada && validarFormatoCampos(elemento, ocurrencia.valor)
      );
      if (formatoInvalido) {
        setValidacion(validarFormatoCampos(elemento, formatoInvalido.valor));
        irARegistro(formatoInvalido.codigo, false);
        return;
      }
      if (elemento.Codigo === "BAN-04") {
        const cuentas = new Set();
        const repetida = ocurrencias.find((ocurrencia) => {
          if (ocurrencia.eliminada) return false;
          const cuenta = String(ocurrencia.valor?.fields?.["BAN-04.CUENTA"] || "");
          if (!cuenta || !cuentas.has(cuenta)) {
            if (cuenta) cuentas.add(cuenta);
            return false;
          }
          return true;
        });
        if (repetida) {
          setValidacion("Cada cuenta bancaria solo puede aparecer una vez. Revisa los registros repetidos.");
          irARegistro(repetida.codigo, false);
          return;
        }
      }
    }

    let filas = ocurrencias;
    if (filas.length === 0 && estadoGrupo === ESTADOS_RESPUESTA.PARA_DESPUES) {
      const omitidaAnterior = ocurrenciasOmitidas[0];
      filas = [{
        codigo: omitidaAnterior
          ? obtenerPrimero(omitidaAnterior, ["CodigoOcurrencia", "codigoOcurrencia"])
          : nuevaOcurrencia(),
        valor: { fields: {} },
        estado: omitidaAnterior ? ESTADOS_RESPUESTA.OMITIDA : ESTADOS_RESPUESTA.SIN_RESPONDER,
        persistida: !!omitidaAnterior,
        sucia: true,
      }];
    }

    let solicitudes;
    if (filas.length === 0 && estadoGrupo === ESTADOS_RESPUESTA.OMITIDA) {
      solicitudes = ocurrenciasOmitidas.length > 0 ? [] : [{
        codigoElemento: elemento.Codigo,
        codigoOcurrencia: CODIGO_OCURRENCIA_OMISION_PREGUNTA,
        estadoRespuesta: ESTADOS_RESPUESTA.OMITIDA,
        contenidoRespuestaJson: null,
        codigoEtapaActual: ubicacion.codigoEtapaActual,
        codigoElementoActual: elemento.Codigo,
        codigoOcurrenciaActual: CODIGO_OCURRENCIA_OMISION_PREGUNTA,
      }];
    } else {
      solicitudes = filas.map((ocurrencia) => {
        const estadoRespuesta = ocurrencia.eliminada
          ? ESTADOS_RESPUESTA.OMITIDA
          : estadoGrupo;
        return {
          codigoElemento: elemento.Codigo,
          codigoOcurrencia: ocurrencia.codigo,
          estadoRespuesta,
          contenidoRespuestaJson: contenidoParaEstado(elemento, ocurrencia.valor, estadoRespuesta),
          codigoEtapaActual: ubicacion.codigoEtapaActual,
          codigoElementoActual: elemento.Codigo,
          codigoOcurrenciaActual: ocurrencia.codigo,
        };
      }).filter((solicitud, indice) => {
        const ocurrencia = filas[indice];
        return !ocurrencia.persistida || ocurrencia.sucia || ocurrencia.estado !== solicitud.estadoRespuesta;
      });
    }

    guardandoGrupoRef.current = true;
    setGuardandoGrupo(true);
    try {
      if (solicitudes.length > 0) await onGuardarVariasYContinuar(solicitudes);
      else await onContinuar();
    } catch (_error) {
      setValidacion("No pudimos guardar todos los registros. Tus cambios siguen aquí; inténtalo de nuevo.");
    } finally {
      guardandoGrupoRef.current = false;
      setGuardandoGrupo(false);
    }
  };
  const hayCambiosSinGuardar = ocurrencias.some((ocurrencia) => ocurrencia.sucia);
  const hayGuardadoEnCurso = ocurrencias.some((ocurrencia) => ocurrencia.guardando);
  const bloqueadoPorOperacionExterna = (guardando && !hayGuardadoEnCurso) || guardandoGrupo;
  const continuarDeshabilitado = ocurrencias.length === 0 && ocurrenciasOmitidas.length === 0;

  return (
    <article
      className="ci-question ci-question--repeatable"
      data-ci-question
      data-ci-dirty={hayCambiosSinGuardar}
      id={`ci-element-${elemento.Codigo}`}
    >
        <h1 className="ci-question__title" id={`ci-title-${elemento.Codigo}`} ref={tituloRef} tabIndex="-1">{elemento.Titulo}</h1>
        {elemento.Ayuda && <p className="ci-question__help">{elemento.Ayuda}</p>}
        {preguntaOmitida ? (
          <Alert variant="secondary">
            <p>Esta pregunta está omitida.</p>
            <Button
              variant="link"
              className="ci-button-link p-0"
              disabled={guardando}
              onClick={() => guardarOcurrencia({
                codigo: CODIGO_OCURRENCIA_OMISION_PREGUNTA,
                valor: null,
              }, ESTADOS_RESPUESTA.SIN_RESPONDER)}
            >
              Reabrir esta pregunta
            </Button>
          </Alert>
        ) : ocurrencias.length === 0 && (
          <Alert variant="light">
            <p>Aún no has agregado registros.</p>
          </Alert>
        )}
        {!preguntaOmitida && ocurrencias.map((ocurrencia, indice) => (
          <section
            className="ci-occurrence"
            id={`ci-occurrence-${elemento.Codigo}-${ocurrencia.codigo}`}
            key={ocurrencia.codigo}
            onFocus={(evento) => {
              if (!evento.currentTarget.contains(evento.relatedTarget)) {
                onActivar(elemento.Codigo, ocurrencia.codigo);
              }
            }}
          >
            <div className="ci-occurrence__heading">
              <h2>{nombreRegistro(elemento, indice)}</h2>
              <div className="ci-occurrence__status">
                {ocurrencia.eliminada || ocurrencia.valor?.fields?.["BAN-02.INACTIVAR"] === true
                  ? <span className="ci-answer-state ci-answer-state--warning">Se eliminará</span>
                  : ocurrencia.guardando
                  ? <span className="ci-answer-state ci-answer-state--warning"><Spinner animation="border" size="sm" className="me-1" />Guardando…</span>
                  : ocurrencia.sucia && ocurrencia.persistida
                  ? <span className="ci-answer-state ci-answer-state--warning">Cambios sin guardar</span>
                  : <EstadoRespuesta estado={ocurrencia.estado} />}
              </div>
            </div>
            {ocurrencia.valor?.fields?.['BAN-02.INACTIVAR'] === true && <p className="ci-retirement-note" role="status">Esta cuenta quedará inactiva al finalizar. Su historial se conservará.</p>}
            {ocurrencia.eliminada && <p className="ci-retirement-note" role="status">Este registro se eliminará del asistente al continuar.</p>}
            <CamposFormulario
              elemento={elemento}
              valor={ocurrencia.valor}
              disabled={bloqueadoPorOperacionExterna || ocurrencia.guardando || ocurrencia.eliminada || ocurrencia.valor?.fields?.["BAN-02.INACTIVAR"] === true}
              idPrefix={`${elemento.Codigo}-${ocurrencia.codigo}`}
              onChange={(valor) => actualizar(ocurrencia.codigo, { valor })}
            />
            {(elemento.Codigo === "BAN-02" || !(String(ocurrencia.codigo || "").startsWith("ERP-") || ocurrencia.origenCitrus === true)) && (
              <div className="ci-answer-actions">
                <Button variant="link" className="ci-button-link" disabled={bloqueadoPorOperacionExterna || hayGuardadoEnCurso} onClick={() => eliminar(ocurrencia)}>
                  {ocurrencia.eliminada || ocurrencia.valor?.fields?.["BAN-02.INACTIVAR"] === true
                    ? "Deshacer eliminación"
                    : "Eliminar"}
                </Button>
              </div>
            )}
          </section>
        ))}
        {validacion && <Alert variant="warning">{validacion}</Alert>}
        {!preguntaOmitida && (
          <Button className="ci-button ci-button--add" variant="outline-primary" disabled={bloqueadoPorOperacionExterna || hayGuardadoEnCurso || (maximo !== null && ocurrencias.length >= maximo)} onClick={agregar}>
            <span className="ci-button--add__icon" aria-hidden="true">+</span>
            {elemento.Codigo === 'BAN-02' ? 'Agregar cuenta bancaria' : elemento.Codigo === 'BAN-04' ? 'Agregar otra cuenta y formato' : elemento.Codigo === 'SED-04' ? 'Agregar sucursal' : 'Agregar registro'}
          </Button>
        )}
        {preguntaOmitida ? (
          <div className="ci-question-footer">
            <button type="button" className="ci-footer-link" disabled={guardando || anteriorDeshabilitado} onClick={onAnterior}>← Anterior</button>
            <Button className="ci-button ci-button--continue" disabled={guardando} onClick={onContinuar}>
              {etiquetaContinuar} <span aria-hidden="true">→</span>
            </Button>
          </div>
        ) : (
          <AccionesRespuesta
            elemento={elemento}
            ocupado={guardando || guardandoGrupo || hayGuardadoEnCurso}
            continuarDeshabilitado={continuarDeshabilitado}
            anteriorDeshabilitado={anteriorDeshabilitado}
            etiquetaContinuar={etiquetaContinuar}
            onAnterior={onAnterior}
            onContinuar={() => guardarGrupo(ESTADOS_RESPUESTA.RESPONDIDA)}
            onDespues={() => guardarGrupo(ESTADOS_RESPUESTA.PARA_DESPUES)}
            onOmitir={() => guardarGrupo(ESTADOS_RESPUESTA.OMITIDA)}
          />
        )}
    </article>
  );
};

export const EditorElementoConfiguracionInicial = ({
  elemento,
  respuestas,
  indiceRespuestas,
  valoresActuales = [],
  sugerencias = [],
  indiceValoresActuales = new Map(),
  indiceSugerencias = new Map(),
  guardando,
  onGuardar,
  onGuardarYContinuar,
  onGuardarVariasYContinuar,
  onActivar,
  ubicacion,
  onAnterior,
  onContinuar,
  etiquetaContinuar,
  anteriorDeshabilitado,
}) => {
  if (elemento.EsRepetible || elemento.TipoRespuesta === "REPEATABLE_FORM") {
    return (
      <ElementoRepetible
        elemento={elemento}
        respuestas={respuestas}
        valoresActuales={valoresActuales}
        sugerencias={sugerencias}
        guardando={guardando}
        onGuardar={onGuardar}
        onGuardarVariasYContinuar={onGuardarVariasYContinuar}
        onActivar={onActivar}
        ubicacion={ubicacion}
        onAnterior={onAnterior}
        onContinuar={onContinuar}
        etiquetaContinuar={etiquetaContinuar}
        anteriorDeshabilitado={anteriorDeshabilitado}
      />
    );
  }
  const respuesta = indiceRespuestas.get(crearClaveRespuesta(elemento.Codigo, CODIGO_OCURRENCIA_UNICA));
  const clave = crearClaveRespuesta(elemento.Codigo, CODIGO_OCURRENCIA_UNICA);
  return (
    <ElementoSimple
      elemento={elemento}
      respuesta={respuesta}
      valorActual={indiceValoresActuales.get(clave)}
      sugerencia={indiceSugerencias.get(clave)}
      guardando={guardando}
      onGuardar={onGuardar}
      onGuardarYContinuar={onGuardarYContinuar}
      onActivar={onActivar}
      ubicacion={ubicacion}
      onAnterior={onAnterior}
      onContinuar={onContinuar}
      etiquetaContinuar={etiquetaContinuar}
      anteriorDeshabilitado={anteriorDeshabilitado}
    />
  );
};
